import { Controller } from '@hotwired/stimulus'
import { Turbo } from '@hotwired/turbo-rails'

type Entry = { url: string, title: string }

export const BACK_NAV_KEY = 'back-nav'
const MAX = 20

// I-UXF-nav-1 global „← Zpět“ (layouts/application): a sessionStorage trail of the in-app pages visited in this tab, of
// one signed-in user (titles name customers: another user starts empty, the sign-in page clears it, back_nav_reset).
// The server says whether the page is a step (record: show/index pages, not edit/new forms) and gives its title.
// A step already on the trail truncates it back to itself (Zpět, browser back, the redirect after a save), the same path
// replaces the last step (tab switch, filters), any other step is pushed; a form page leaves the trail as it is.
// Zpět visits the last step that is not this page; without one the link keeps the server's href (return_to, else the
// breadcrumb parent).
export default class BackNavController extends Controller<HTMLAnchorElement> {
  static override targets = ['label']
  static override values = { record: Boolean, title: String, prefix: String, user: String }
  declare readonly labelTarget: HTMLElement
  declare readonly recordValue: boolean
  declare readonly titleValue: string
  declare readonly prefixValue: string
  declare readonly userValue: string

  private previous: Entry | undefined

  override connect(): void {
    this.sync()
  }

  // Also on turbo:render (a morph keeps this element without a new connect but resets its href, label and hidden to the
  // server's) and on turbo:frame-render (a frame with turbo-action advance, e.g. the audit log filter, changes the URL).
  sync(): void {
    if (document.documentElement.hasAttribute('data-turbo-preview')) return

    const here = location.pathname + location.search
    let trail = this.load()
    if (this.recordValue) {
      const index = trail.findIndex((entry) => entry.url === here)
      if (index >= 0) trail = trail.slice(0, index + 1)
      else {
        const last = trail.at(-1)
        if (last && new URL(last.url, location.origin).pathname === location.pathname) trail.pop()
        trail = [...trail, { url: here, title: this.titleValue }].slice(-MAX)
      }
      this.save(trail)
    }

    this.previous = [...trail].reverse().find((entry) => entry.url !== here)
    if (!this.previous) return

    this.element.href = this.previous.url
    this.element.hidden = false
    if (this.previous.title) {
      this.labelTarget.textContent = this.previous.title
      this.element.setAttribute('aria-label', `${this.prefixValue} ${this.previous.title}`)
    }
  }

  visit(event: MouseEvent): void {
    if (!this.previous || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return

    event.preventDefault()
    Turbo.visit(this.previous.url)
  }

  private load(): Entry[] {
    try {
      const value: unknown = JSON.parse(sessionStorage.getItem(BACK_NAV_KEY) ?? '{}')
      if (typeof value !== 'object' || value === null || !('user' in value) || value.user !== this.userValue) return []
      const trail = 'trail' in value ? value.trail : []
      return Array.isArray(trail) ? trail.filter((entry): entry is Entry => typeof entry?.url === 'string') : []
    } catch {
      return []
    }
  }

  private save(trail: Entry[]): void {
    try {
      sessionStorage.setItem(BACK_NAV_KEY, JSON.stringify({ user: this.userValue, trail }))
    } catch {
      // storage full or disabled: Zpět keeps the server's href
    }
  }
}
